const replaceAll = (str, find, replace) => {
  const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  return str.replace(new RegExp(escapedFind, 'g'), replace);
};

export const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const addHours = (date, hours) => {
  const d = new Date(date);
  d.setHours(d.getHours() + hours);
  return d;
};

export const differenceInHours = (date1, date2) => {
  return (new Date(date1) - new Date(date2)) / 36e5;
};

export const isSameDay = (d1, d2) => {
  const a = new Date(d1);
  const b = new Date(d2);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

export const formatDate = (date, pattern, locale = 'en-US') => {
  const d = new Date(date);
  switch (pattern) {
    case 'yyyy-MM-dd':
      return d.toISOString().slice(0, 10);
    case 'HH:mm':
      return d.toISOString().slice(11, 16);
    case 'HH:mm:ss':
      return d.toISOString().slice(11, 19);
    case "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'":
      return d.toISOString();
    case 'dd MMM':
      return d
        .toLocaleDateString(locale, { day: '2-digit', month: 'short' })
        .replace('.', '');
    case 'EEE':
      return d.toLocaleDateString(locale, { weekday: 'short' });
    default:
      return d.toLocaleDateString(locale);
  }
};

export const formatDateLocal = (date, pattern) => {
  const d = new Date(date);
  switch (pattern) {
    case 'yyyy-MM-dd':
      return `${d.getFullYear()}-${(d.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
    case 'HH:mm':
      return `${d.getHours().toString().padStart(2, '0')}:${d
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
    case 'HH:mm:ss':
      return `${d.getHours().toString().padStart(2, '0')}:${d
        .getMinutes()
        .toString()
        .padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
    case 'dd MMM':
      return d
        .toLocaleDateString('en-US', { day: '2-digit', month: 'short' })
        .replace('.', '');
    case 'EEE':
      return d.toLocaleDateString('en-US', { weekday: 'short' });
    default:
      return d.toLocaleDateString();
  }
};

export const parseISO = (isoStr) => new Date(isoStr);

export const isValidDate = (value) => {
  const d = new Date(value);
  return !Number.isNaN(d.getTime());
};

export const getISODate = (date) => new Date(date).toISOString().split('T')[0];

export const formatDateLocale = (date, locale = 'en-US', options) => {
  const d = new Date(date);
  return d.toLocaleDateString(locale, options);
};

/**
 * Format a date by a desired format
 * @param {Date|string} inputDate - Date to format
 * @param {string} desiredFormat - Desired format
 * @param {string} locale - Locale to use
 * @returns {string} - Formatted date
 */
export const formatDateByFormat = (
  inputDate,
  desiredFormat,
  locale = 'en-US',
  useUTC = false
) => {
  const dateObject =
    inputDate instanceof Date ? inputDate : new Date(inputDate);
  const year = useUTC ? dateObject.getUTCFullYear() : dateObject.getFullYear();
  const month = useUTC ? dateObject.getUTCMonth() : dateObject.getMonth(); // Month is zero-based
  const day = useUTC ? dateObject.getUTCDate() : dateObject.getDate();
  const hours = useUTC ? dateObject.getUTCHours() : dateObject.getHours();
  const minutes = useUTC ? dateObject.getUTCMinutes() : dateObject.getMinutes();
  const seconds = useUTC ? dateObject.getUTCSeconds() : dateObject.getSeconds();

  const formatTokens = {
    YYYY: year,
    MMMM: dateObject.toLocaleString(locale, { month: 'long' }),
    MMM: dateObject.toLocaleString(locale, { month: 'short' }),
    MM: (month + 1).toString().padStart(2, '0'), // Increment month by 1
    DD: day.toString().padStart(2, '0'),
    HH: hours.toString().padStart(2, '0'),
    hh: hours.toString().padStart(2, '0'),
    mm: minutes.toString().padStart(2, '0'),
    ss: seconds.toString().padStart(2, '0'),
    '[de]': 'de',
    '[T]': 'T',
    '[Z]': 'Z',
  };

  let formattedDate = desiredFormat;

  for (const token in formatTokens) {
    formattedDate = replaceAll(formattedDate, token, formatTokens[token]);
  }

  return formattedDate?.replace('.', '');
};

export const formatTime = (
  date,
  locale = 'en-US',
  options = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  },
  useUTC = false
) => {
  const d = new Date(date);

  if (useUTC) {
    // Formata usando UTC diretamente
    const hours = d.getUTCHours().toString().padStart(2, '0');
    const minutes = d.getUTCMinutes().toString().padStart(2, '0');
    const seconds =
      options.second === '2-digit'
        ? d.getUTCSeconds().toString().padStart(2, '0')
        : '';

    if (options.hour12) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours =
        hours > 12 ? hours - 12 : hours === '00' ? 12 : hours;
      return `${displayHours.toString().padStart(2, '0')}:${minutes}${seconds ? `:${seconds}` : ''} ${period}`;
    }

    return `${hours}:${minutes}${seconds ? `:${seconds}` : ''}`;
  }

  return d.toLocaleTimeString(locale, options);
};

/**
 * Extrai o horário UTC diretamente de uma string ISO, sem aplicar conversão de timezone
 * Útil quando você quer exibir o horário UTC como está no backend
 * @param {string} dateString - Data no formato ISO com timezone (ex: '2025-11-10T18:00:00.000Z')
 * @returns {Date} - Date object com o horário UTC preservado
 */
export const parseUTCDate = (dateString) => {
  if (!dateString) return null;

  const dateStr = String(dateString);

  // Extrai os componentes da data diretamente da string
  const match = dateStr.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/
  );

  if (!match) {
    // Se não conseguir fazer parse, usa new Date normalmente
    return new Date(dateStr);
  }

  const [, year, month, day, hour, minute, second, millisecond] = match;

  // Cria a data usando UTC diretamente (sem conversão para local)
  return new Date(
    Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1, // Mês é 0-indexed
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10),
      millisecond ? parseInt(millisecond, 10) : 0
    )
  );
};

/**
 * Converte uma data, tratando corretamente datas com e sem timezone
 * Se a data já tem timezone (Z, +, -), extrai o horário UTC diretamente sem conversão
 * Se não tem timezone, assume UTC — o Date resultante converte para local automaticamente na leitura
 * @param {string|Date} dateString - Data no formato ISO (ex: '2025-11-10T18:00:00.000Z' ou '2025-11-10T23:00:00')
 * @returns {Date} - Date object
 */
export const parseDateWithOffset = (dateString) => {
  if (!dateString) return null;

  // Se já é uma Date, retorna diretamente
  if (dateString instanceof Date) {
    return new Date(dateString);
  }

  const dateStr = String(dateString);

  // Se a data já tem timezone (Z, +, ou - no final), extrai UTC diretamente
  const hasTimezone =
    dateStr.includes('Z') ||
    /[+-]\d{2}:?\d{2}$/.test(dateStr) ||
    /[+-]\d{4}$/.test(dateStr);

  if (hasTimezone) {
    // Extrai o horário UTC diretamente da string, sem aplicar conversão de timezone
    return parseUTCDate(dateStr);
  }

  // Se não tem timezone, assume UTC. O objeto Date guarda o instante em UTC
  // internamente e qualquer leitura local (getHours, toLocaleString, etc.)
  // já converte para o horário local automaticamente — não aplicar offset manual aqui,
  // ou o horário é deslocado duas vezes (bug: 12:30 BRT exibido como 09:30).
  const isoString = `${dateStr}Z`;
  return new Date(isoString);
};
